import { irPosts } from '@/data/ir.generated'
import { IRContent, irLink, irDate } from '@/components/ui/IRContent'
import { t } from '@/utils/i18n'
import { parsePath } from '@/utils/lang'
import { isIRAnnouncementActive, nextKoreanMidnight, irHideKey, IR_SESSION_KEY } from '@/utils/irAnnouncement'
import { makeNoticeMovable } from '@/utils/movableNotice'
import type { IRPost } from '@/types/ir'

const memory = new Map<string, string>()
function read(storage: 'localStorage' | 'sessionStorage', key: string): string | null {
  try { return window[storage].getItem(key) ?? memory.get(`${storage}:${key}`) ?? null }
  catch { return memory.get(`${storage}:${key}`) ?? null }
}
function write(storage: 'localStorage' | 'sessionStorage', key: string, value: string): void {
  memory.set(`${storage}:${key}`, value)
  try { window[storage].setItem(key, value) } catch { /* Closing must work even when storage is disabled. */ }
}

/** All time-limited UI is live-only; the IR archive remains available in snapshots. */
export function mountIRAnnouncement(): void {
  document.getElementById('ir-announcement')?.remove()
  if ((window as Window & { __GIGAETTE_PRERENDER__?: boolean }).__GIGAETTE_PRERENDER__) return
  let generation = 0
  let current: HTMLDialogElement | null = null
  let expiryTimer: ReturnType<typeof setTimeout> | undefined

  const open = async (post: IRPost) => {
    if (current || !isIRAnnouncementActive(post)) return
    const request = ++generation
    const { irBodies } = await import('@/data/irBodies.generated')
    if (request !== generation || parsePath(window.location.pathname).logicalPath !== '/' || !isIRAnnouncementActive(post)) return
    const body = irBodies[post.slug]
    if (!body) return
    // The public notice popup always presents the Korean original without
    // changing the surrounding site's language or the IR destination route.
    const popupText = (key: string) => t(key, 'ko')
    const dialog = document.createElement('dialog')
    // Unsupported dialog browsers retain the working HTML notice link.
    if (typeof dialog.show !== 'function') return
    dialog.id = 'ir-announcement'
    dialog.lang = 'ko'
    dialog.dataset.slug = post.slug
    dialog.dataset.trigger = 'automatic'
    dialog.className = 'fixed left-4 top-24 bottom-auto right-auto z-[70] m-0 w-[calc(100%-2rem)] max-w-xl max-h-[80dvh] overflow-hidden rounded-2xl border border-slate-200 bg-white p-0 text-slate-900 shadow-2xl open:flex open:flex-col [@media(max-height:480px)]:overflow-y-auto'
    dialog.setAttribute('aria-labelledby', 'ir-announcement-title')
    dialog.setAttribute('aria-modal', 'false')
    const top = document.createElement('div')
    top.className = 'flex items-start gap-4 border-b border-slate-200 px-5 py-4 sm:px-8 sm:py-5 shrink-0'
    const headings = document.createElement('div')
    headings.className = 'flex-1 min-w-0 cursor-grab touch-none select-none rounded outline-0 [outline-style:none]'
    headings.dataset.dragHandle = ''
    headings.tabIndex = 0
    headings.setAttribute('role', 'button')
    headings.setAttribute('aria-label', popupText('ir.moveNotice'))
    headings.setAttribute('aria-describedby', 'ir-move-instructions')
    const title = document.createElement('h2')
    title.id = 'ir-announcement-title'
    title.tabIndex = -1
    title.className = 'text-xl sm:text-2xl font-semibold leading-relaxed outline-0 [outline-style:none]'
    title.textContent = post.title.ko
    const date = document.createElement('time')
    date.dateTime = post.date
    date.className = 'block text-sm text-slate-600 mt-2'
    date.textContent = `${popupText('ir.published')} ${irDate(post.date, 'ko')}`
    const instructions = document.createElement('span')
    instructions.id = 'ir-move-instructions'
    instructions.className = 'sr-only'
    instructions.textContent = popupText('ir.moveInstructions')
    headings.append(title, date, instructions)
    const closeIcon = document.createElement('button')
    closeIcon.type = 'button'
    closeIcon.className = 'inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-2xl text-slate-600 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-700'
    closeIcon.setAttribute('aria-label', popupText('ir.close'))
    closeIcon.textContent = '×'
    top.append(headings, closeIcon)
    const scroll = document.createElement('div')
    scroll.className = 'overflow-y-auto overscroll-contain px-5 py-6 sm:px-8 min-h-0 outline-0 [outline-style:none] [@media(max-height:480px)]:overflow-visible [@media(max-height:480px)]:shrink-0 [@media(max-height:480px)]:py-4'
    scroll.tabIndex = 0
    scroll.setAttribute('role', 'region')
    scroll.setAttribute('aria-label', post.title.ko)
    scroll.appendChild(IRContent(post, body.ko, { showAttachment: false, language: 'ko' }))
    const bottom = document.createElement('div')
    bottom.className = 'flex items-center gap-2 border-t border-slate-200 px-3 py-3 sm:px-8 sm:py-4 shrink-0'
    const options = document.createElement('label')
    options.className = 'inline-flex min-w-0 min-h-11 flex-1 items-center gap-1.5 text-xs text-slate-600 cursor-pointer sm:gap-3 sm:text-sm'
    const hideToday = document.createElement('input')
    hideToday.type = 'checkbox'
    hideToday.className = 'h-4 w-4 shrink-0 accent-orange-700 sm:h-5 sm:w-5'
    options.append(hideToday, popupText('ir.hideToday'))
    const actions = document.createElement('div')
    actions.className = 'ml-auto flex shrink-0 items-center justify-end gap-1.5 sm:gap-3'
    const detail = irLink(`/ir/${post.slug}`, popupText('ir.viewPost'), 'inline-flex min-h-11 items-center justify-center whitespace-nowrap rounded-lg bg-orange-700 px-2 py-2 text-xs font-semibold text-white hover:bg-orange-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-700 sm:px-5 sm:py-3 sm:text-sm')
    const closeButton = document.createElement('button')
    closeButton.type = 'button'
    closeButton.textContent = popupText('ir.close')
    closeButton.className = 'inline-flex min-h-11 items-center justify-center whitespace-nowrap rounded-lg border border-slate-300 px-2 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-700 sm:px-5 sm:py-3 sm:text-sm'
    actions.append(detail, closeButton)
    bottom.append(options, actions)
    dialog.append(top, scroll, bottom)
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const consent = document.getElementById('consent-banner')
    const consentWasHidden = consent?.hidden ?? false
    let restoreFocus = false
    let stopMoving: (() => void) | undefined
    const close = () => {
      restoreFocus = dialog.contains(document.activeElement)
      dialog.close()
    }
    closeIcon.addEventListener('click', close)
    closeButton.addEventListener('click', close)
    dialog.addEventListener('keydown', event => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      event.stopPropagation()
      close()
    })
    dialog.addEventListener('close', () => {
      if (hideToday.checked) write('localStorage', irHideKey(post), String(nextKoreanMidnight()))
      clearTimeout(expiryTimer)
      stopMoving?.()
      if (consent?.isConnected) consent.hidden = consentWasHidden
      dialog.remove()
      current = null
      if (restoreFocus && previousFocus?.isConnected) previousFocus.focus({ preventScroll: true })
    }, { once: true })
    document.body.appendChild(dialog)
    current = dialog
    dialog.show()
    // Defer competing cookie controls without making the homepage inert or locked.
    if (consent) consent.hidden = true
    stopMoving = makeNoticeMovable(dialog, headings)
    previousFocus?.focus({ preventScroll: true })
    write('sessionStorage', IR_SESSION_KEY, 'shown')
    const scheduleExpiry = () => {
      const remaining = Date.parse(post.popup!.until) - Date.now()
      if (remaining <= 0) close()
      else expiryTimer = setTimeout(scheduleExpiry, Math.min(remaining, 2147483647))
    }
    scheduleExpiry()
  }

  const refresh = () => {
    if (document.visibilityState === 'hidden' || parsePath(window.location.pathname).logicalPath !== '/') return
    const active = irPosts.filter(post => isIRAnnouncementActive(post))
    if (current && !active.some(post => current?.dataset.slug === post.slug)) current.close()
    if (read('sessionStorage', IR_SESSION_KEY) === 'shown') return
    const candidate = active.find(post => {
      const hiddenUntil = Number(read('localStorage', irHideKey(post)))
      return !Number.isFinite(hiddenUntil) || hiddenUntil <= Date.now()
    })
    if (candidate) void open(candidate).catch(() => { /* IR remains accessible if a lazy chunk fails. */ })
  }
  window.addEventListener('route-will-change', () => {
    generation++
    if (current) current.close()
  })
  window.addEventListener('route-rendered', refresh)
  window.addEventListener('focus', refresh)
  document.addEventListener('visibilitychange', refresh)
  // Includes start/end boundaries in tabs left open; dialogs also expire precisely.
  window.setInterval(refresh, 60_000)
}
